import { TrendingUp, Mail, Rss } from "lucide-react";
import { Button } from "@/components/ui/button";
import { handleAffiliateClick } from "@/lib/affiliate";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const Footer = ({ onTabChange }: { onTabChange: (tab: string) => void }) => {
  const { isAdmin, signOut } = useAuth();
  const rssUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-rss`;

  const footerLinks = {
    company: [
      { label: "About Us", href: "/about" },
      { label: "Editorial Policy", href: "/editorial-policy" },
      { label: "Testing Methodology", href: "/prop-firm-testing-methodology" },
      { label: "Contact Us", href: "/contact" },
    ],
    legal: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms of Service", href: "/terms-of-service" },
      { label: "Cookie Policy", href: "/cookie-policy" },
      { label: "Affiliate Disclosure", href: "/affiliate-disclosure" },
      { label: "Disclaimer", href: "/disclaimer" },
      { label: "Risk Disclosure", href: "/risk-disclosure" },
    ],
    propFirms: [
      { label: "All Prop Firm Reviews", href: "/prop-firms" },
      { label: "Best Futures Prop Firms", href: "/best-futures-prop-firms" },
      { label: "Apex Resource Hub", href: "/apex-trader-funding-hub" },
      { label: "Apex Alternatives", href: "/apex-alternatives" },
      { label: "Is Apex Legit?", href: "/is-apex-trader-funding-legit" },
    ],
    calculators: [
      { label: "Apex Calculators Hub", href: "/apex-calculators" },
      { label: "Apex Drawdown Calculator", href: "/apex-trailing-drawdown-calculator" },
      { label: "Tick Value Calculator", href: "/calculators/tick-value" },
      { label: "Position Size Calculator", href: "/calculators/position-size" },
      { label: "Risk / Reward Calculator", href: "/calculators/risk-reward" },
    ],
    trading: [
      { label: "Trading Education", href: "/blog" },
      { label: "Funded Trader Guide", href: "/funded-trader-guide" },
      { label: "How to Pass a Prop Firm Eval", href: "/how-to-pass-prop-firm-evaluation" },
      { label: "Trading Psychology Guide", href: "/futures-trading-psychology-guide" },
      { label: "Futures Glossary", href: "/futures-trading-glossary" },
{ label: "Funded Accounts (Code: Next-Level)", href: "#", onClick: handleAffiliateClick },
    ],
    // Apex keyword anchors — sitewide internal linking, compact 2-col grid
    apexKeywords: [
      { label: "Apex Trader Funding 90% Off", href: "/apex-trader-funding-review" },
      { label: "Apex Trader Funding Login", href: "/apex-trader-funding-review#apex-90-off-login" },
      { label: "Apex Consistency Rule", href: "/apex-consistency-rule-explained" },
      { label: "Apex Drawdown Calculator", href: "/apex-trailing-drawdown-calculator" },
      { label: "Apex Payout Proof", href: "/apex-payout-proof" },
      { label: "How Hard Is Apex?", href: "/how-hard-is-apex-trader-funding" },
      { label: "Apex Trader Funding Rules", href: "/apex-trader-funding-rules" },
      { label: "Apex Coupon & Discount Code", href: "/apex-trader-funding-coupon-code" },
      { label: "Apex Evaluation Guide", href: "/apex-trader-funding-evaluation" },
      { label: "Apex Platforms (Tradovate / NT / Rithmic)", href: "/apex-trader-funding-platforms" },
      { label: "Apex Funding Login", href: "/apex-trader-funding-review#apex-90-off-login" },
      { label: "Apex Alternatives", href: "/apex-alternatives" },
    ],
  };

  const renderLink = (link: { label: string; href: string; onClick?: (e: React.MouseEvent) => void }) => (
    link.href.startsWith('/') ? (
      <Link
        to={link.href}
        className="text-xs text-muted-foreground hover:text-trading-primary transition-colors"
      >
        {link.label}
      </Link>
    ) : (
      <button
        onClick={link.onClick}
        className="text-xs text-muted-foreground hover:text-trading-primary transition-colors text-left"
      >
        {link.label}
      </button>
    )
  );

  return (
    <footer className="bg-card border-t border-border mt-auto">
      {/* Affiliate CTA bar */}
      <div className="bg-primary/10 border-b border-border">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <Button
            variant="affiliate"
            size="sm"
            onClick={handleAffiliateClick}
            style={{ cursor: 'pointer' }}
          >
            🚀 Apex Trader Funding 90% Off — Claim Today
          </Button>
          <span className="text-xs text-muted-foreground">
            Use code <strong className="text-foreground">Next-Level</strong> for the largest Apex discount of the year
          </span>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10">
        {/* Main link grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">

          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <div className="flex items-center space-x-2 mb-3">
              <TrendingUp className="h-6 w-6 text-trading-primary" />
              <span className="text-base font-bold text-foreground">Futures Trader Hub</span>
            </div>
            <p className="text-muted-foreground text-xs mb-3 leading-relaxed">
              Independent futures prop-firm rule analysis, trading education, and risk-management tools.
            </p>
            <div className="flex items-center text-xs text-muted-foreground">
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              support@futures-trader-hub.com
            </div>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-foreground font-semibold text-xs uppercase tracking-wider mb-3">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link, index) => (
                <li key={index}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Trading */}
          <div>
            <h3 className="text-foreground font-semibold text-xs uppercase tracking-wider mb-3">Trading</h3>
            <ul className="space-y-2">
              {footerLinks.trading.map((link, index) => (
                <li key={index}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Prop Firms */}
          <div>
            <h3 className="text-foreground font-semibold text-xs uppercase tracking-wider mb-3">Prop Firms</h3>
            <ul className="space-y-2">
              {footerLinks.propFirms.map((link, index) => (
                <li key={index}>{renderLink(link)}</li>
              ))}
              <li>
                <a
                  href={rssUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-trading-primary transition-colors inline-flex items-center"
                >
                  <Rss className="h-3 w-3 mr-1" />
                  RSS Feed
                </a>
              </li>
            </ul>
          </div>

          {/* Calculators */}
          <div>
            <h3 className="text-foreground font-semibold text-xs uppercase tracking-wider mb-3">Calculators</h3>
            <ul className="space-y-2">
              {footerLinks.calculators.map((link, index) => (
                <li key={index}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Apex Trader Funding — keyword anchors, compact 2-col grid */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <h3 className="text-trading-primary font-semibold text-xs uppercase tracking-wider mb-3">Apex Trader Funding</h3>
            <div className="grid grid-cols-1 gap-x-4 gap-y-2">
              {footerLinks.apexKeywords.map((link, index) => (
                <div key={index}>{renderLink(link)}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Legal + bottom bar */}
        <div className="border-t border-border mt-8 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2">
              {footerLinks.legal.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="text-xs text-muted-foreground hover:text-trading-primary transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center space-x-2">
              {isAdmin ? (
                <>
                  <button
                    onClick={() => onTabChange("admin")}
                    className="text-xs text-trading-primary hover:text-trading-primary/80 transition-colors"
                  >
                    Admin Panel
                  </button>
                  <span className="text-xs text-muted-foreground">|</span>
                  <button
                    onClick={signOut}
                    className="text-xs text-muted-foreground hover:text-trading-primary transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => onTabChange("admin")}
                  className="text-xs text-muted-foreground hover:text-trading-primary transition-colors"
                >
                  Admin
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-2 mt-4">
            <div className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Futures Trader Hub. All rights reserved.
            </div>
            <p className="text-[11px] text-muted-foreground/80 leading-relaxed md:text-right md:max-w-2xl">
              <strong>Risk Disclaimer:</strong> Futures and options trading has large potential rewards, but also large potential risk.
              Past performance is not necessarily indicative of future results. This website is neither a solicitation nor an offer to Buy/Sell futures or options.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
